// Types
import type { Order } from "@/types/supabase";

// Libs
import { useState } from "react";
import { toastHandler } from "@/lib/ToastHandler";

// Hooks
import { useAssignation } from "@/hooks/useAssignation";

// Components
import { Button } from "@/components/atoms/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from "@/components/atoms/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/atoms/avatar";
import { Badge } from "@/components/atoms/badge";

// Icons
import { Loader2, AlertTriangle, CheckCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

interface IdleDriverResponse {
  id: number;
  name: string | null;
  branch_id: number | null;
  vehicle_type: string | null;
  worker_type: string | null;
  picture: string | null;
  is_checked_in?: boolean;
}

interface DriverAssignmentConfirmationProps {
  trigger: React.ReactNode;
  order: Order;
  selectedDriver: IdleDriverResponse;
  onClose?: () => void;
}

export default function DriverAssignmentConfirmation({
  trigger,
  order,
  selectedDriver,
  onClose,
}: DriverAssignmentConfirmationProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const { postManualAssignment } = useAssignation();
  const { supabaseInfo } = useAuth();
  const requiresCheckInOverride = selectedDriver.is_checked_in !== true;
  const handleConfirmAssignment = async () => {
    try {
      setIsAssigning(true);

      const userId = Number(supabaseInfo?.id) || 0;
      await postManualAssignment({
        order_id: order.id,
        driver_id: selectedDriver.id,
        user_id: userId,
      });

      // Show success message
      toastHandler({
        title: "Conductor asignado",
        description: `${selectedDriver.name} ha sido asignado a la orden #${order.id}`,
        variant: "success",
      });

      // Close dialog
      setIsDialogOpen(false);
      onClose?.();
    } catch (error) {
      console.error("Error assigning driver:", error);
      toastHandler({
        title: "Error al asignar conductor",
        description: "No se pudo completar la asignación. Inténtalo de nuevo.",
        variant: "destructive",
      });
    } finally {
      setIsAssigning(false);
    }
  };

  const handleCancel = () => {
    setIsDialogOpen(false);
    onClose?.();
  };

  const getVehicleTypeDisplay = (vehicleType: string | null) => {
    switch (vehicleType) {
      case "car":
        return "Automóvil";
      case "motorcycle":
        return "Moto";
      case "moto":
        return "Moto";
      case "bicycle":
        return "Bicicleta";
      default:
        return vehicleType || "N/A";
    }
  };

  const getWorkerTypeDisplay = (workerType: string | null) => {
    switch (workerType) {
      case "on_demand":
        return "On-demand";
      case "full_time":
        return "Full-time";
      case "part_time":
        return "Part-time";
      default:
        return workerType
          ? workerType.charAt(0).toUpperCase() + workerType.slice(1)
          : "N/A";
    }
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        className="bg-white max-w-md mx-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Confirmar asignación
          </DialogTitle>
          <DialogDescription className="text-gray-600">
            ¿Estás seguro de que quieres asignar este conductor a la orden?
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Order Information */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-2">
              Orden #{order.id}
            </h4>
            <div className="text-sm text-gray-600 space-y-1">
              <p>
                <span className="font-medium">Sucursal:</span>{" "}
                {order.branch?.name || "N/A"}
              </p>
              <p>
                <span className="font-medium">Tracking ID:</span>{" "}
                {order.tracking_id}
              </p>
              <p>
                <span className="font-medium">Estado actual:</span>
                <Badge variant="outline" className="ml-2">
                  {order.status}
                </Badge>
              </p>
            </div>
          </div>

          {/* Driver Information */}
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-blue-600" />
              Conductor seleccionado
            </h4>
            <div className="flex items-center gap-3">
              <Avatar className="w-12 h-12">
                <AvatarImage
                  src={
                    selectedDriver.picture ||
                    "/driver-illustration/profile-pic-default.svg"
                  }
                />
                <AvatarFallback>
                  <div className="w-12 h-12 rounded-full bg-blue-100 grid place-items-center text-sm font-medium text-blue-700">
                    {selectedDriver.name?.substring(0, 2).toUpperCase()}
                  </div>
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">
                  {selectedDriver.name}
                </p>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  <Badge variant="secondary" className="text-xs">
                    {getVehicleTypeDisplay(selectedDriver.vehicle_type)}
                  </Badge>
                  <Badge variant="outline" className="text-xs">
                    {getWorkerTypeDisplay(selectedDriver.worker_type)}
                  </Badge>
                </div>
              </div>
            </div>
          </div>

          {/* Warning Message */}
          {requiresCheckInOverride && (
            <div
              className="bg-amber-100 border border-amber-400 rounded-lg p-3"
              role="alert"
            >
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
                <p className="text-sm font-medium text-amber-900">
                  Este conductor está conectado, pero no tiene un check-in
                  activo en la sucursal. La asignación manual omitirá esa regla.
                  Confirma solo si el despachador ya verificó su disponibilidad.
                </p>
              </div>
            </div>
          )}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-amber-800">
                Esta acción cambiará el estado de la orden a "Asignado" y no se
                puede deshacer fácilmente.
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:gap-3">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isAssigning}
            className="w-full sm:w-auto"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmAssignment}
            disabled={isAssigning}
            className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            {isAssigning ? (
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Asignando...
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                {requiresCheckInOverride
                  ? "Confirmar sin check-in"
                  : "Confirmar asignación"}
              </div>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
